import React, { cloneElement } from 'react'
import classnames from 'classnames'
import { Invites } from '../../../../types/registration'
import { parseTesaVersion, isNgomOrVnemTenantName } from '../constants'

interface OnboardingStepsProps {
  className?: string
  children: Array<React.ReactElement<any, string | React.JSXElementConstructor<any>> | null>
}

const OnboardingSteps: React.FC<OnboardingStepsProps> = (props) => {
  const validChildren = props.children.filter((c): c is React.ReactElement => c != null)
  // Each child's incoming `isActive` prop is really "is this stage reached" — render based on
  // each step's own reached flag rather than position so non-linear flows (e.g., NGOM accounts
  // marked complete with no TESA / PMA on file) render correctly as
  // [x] Invitation generated, [ ] TESA, [ ] PMA, [x] Registration complete instead of incorrectly
  // back-filling the skipped stages.
  const reversedActiveIndex = validChildren
    .slice()
    .reverse()
    .findIndex((c: any) => c.props.isActive)
  const lastReachedIndex = reversedActiveIndex >= 0 ? validChildren.length - 1 - reversedActiveIndex : -1

  const children = validChildren.map((OnboardingStep, i) => {
    const isReached = !!(OnboardingStep as any).props.isActive
    return cloneElement(OnboardingStep, {
      key: i,
      number: i + 1,
      isComplete: isReached && i !== lastReachedIndex,
      isActive: isReached && i === lastReachedIndex,
      isIncomplete: !isReached,
    })
  })

  return <div className={classnames('flex border-indigo-200 dark:border-indigo-700 border-r-1', props.className)}>{children}</div>
}

interface OnboardingStepProps {
  isComplete?: boolean
  isActive?: boolean
  isIncomplete?: boolean
}

const OnboardingStep: React.FC<OnboardingStepProps> = (props) => {
  const { isComplete, isActive, isIncomplete } = props

  return (
    <div
      className={classnames('w-8 h-2.5 border border-indigo-400 dark:border-indigo-500/70 border-r-0 last:border-r first:rounded-l last:rounded-r', {
        'bg-indigo-200 dark:bg-indigo-900': isComplete,
        'bg-indigo-400 dark:bg-indigo-700': isActive,
        'bg-transparent': isIncomplete,
      })}
    />
  )
}

interface OnboardingStepDescProps {
  isActive: boolean
  children?: React.ReactNode
}

const OnboardingStepDesc: React.FC<OnboardingStepDescProps> = (props) => {
  const { children, isActive } = props

  if (!isActive) {
    return null
  }

  return <div className={classnames('text-sm font-base')}>{children}</div>
}

export interface OnboardingMiniTimelineProps {
  invites?: Invites
  /** Tenant business display name — used to detect NGOM/VNEM records and hide TESA + PMA steps. */
  tenantName?: string | null
  /** When true, TESA and PMA steps are omitted (National accounts are agreement-exempt). */
  isNational?: boolean
}

export const OnboardingMiniTimeline: React.FC<OnboardingMiniTimelineProps> = (props) => {
  const {
    ONBOARDING_REGISTRATION,
    isOnregInviteCreated = false,
    isOnregInvitePending = false,
    isOnregAgreedTesa = false,
    isOnregAgreedTesaSupplement = false,
    isOnregAgreedPricingAndPayments = false,
    isOnregInviteConsumed = false,
    isAccountInviteCreated = false,
    isAccountInviteConsumed = false,
  } = props.invites ?? {}

  const tesaVersion = ONBOARDING_REGISTRATION?.tesaVersion ?? null
  const { isLegacy: isLegacyFlow } = parseTesaVersion(tesaVersion)

  const tesaAcceptedAt = ONBOARDING_REGISTRATION?.agreements?.find((a) => a.type === 'TESA')?.acceptedAt
  const supplementAcceptedAt = ONBOARDING_REGISTRATION?.agreements?.find((a) => a.type === 'TESA_SUPPLEMENT')?.acceptedAt
  const paymentAcceptedAt = ONBOARDING_REGISTRATION?.agreements?.find((a) => a.type === 'PRICING_AND_PAYMENT_TERMS')?.acceptedAt
  const hasExternalSupplement =
    ONBOARDING_REGISTRATION?.externallySignedAgreementDocuments?.some((d) => d.type === 'TESA_SUPPLEMENT') ?? false

  // NGOM/VNEM accounts (KE-internal solar production / VNEM allocation entities) and National
  // accounts don't require TESA or PMA, so we omit those steps from the timeline entirely instead
  // of showing them as perpetually-incomplete. "Tenant created" is still shown — the tenant
  // entity is needed for meter / property association even on NGOM.
  const isNgom = isNgomOrVnemTenantName(props.tenantName)
  const isAgreementExempt = isNgom || props.isNational === true
  const showPaymentStep = !isAgreementExempt && (isLegacyFlow || !!paymentAcceptedAt)

  const stepInviteGenerated = isOnregInviteCreated || !!ONBOARDING_REGISTRATION?.createDate
  const stepTesaAccepted = !!tesaAcceptedAt || (isOnregInvitePending && isOnregAgreedTesa)
  const stepSupplementAccepted = !!supplementAcceptedAt || hasExternalSupplement || (isOnregInvitePending && isOnregAgreedTesaSupplement)
  const stepPaymentAccepted = !!paymentAcceptedAt || (isOnregInvitePending && isOnregAgreedPricingAndPayments)
  // Aligned with invitation.consumed only — supports NGOM accounts marked complete with no TESA / PMA.
  const stepRegistrationComplete = isOnregInviteConsumed

  return (
    <div>
      <OnboardingSteps>
        <OnboardingStep isActive={stepInviteGenerated} />
        {!isAgreementExempt ? <OnboardingStep isActive={stepTesaAccepted} /> : null}
        {!isAgreementExempt ? <OnboardingStep isActive={stepSupplementAccepted} /> : null}
        {showPaymentStep ? <OnboardingStep isActive={stepPaymentAccepted} /> : null}
        <OnboardingStep isActive={stepRegistrationComplete} />
        <OnboardingStep isActive={isAccountInviteCreated} />
        {!isNgom ? <OnboardingStep isActive={isAccountInviteConsumed} /> : null}
      </OnboardingSteps>
      <OnboardingSteps className="mt-0.5 font-medium">
        <OnboardingStepDesc isActive={stepInviteGenerated}>Registration invitation generated</OnboardingStepDesc>
        {!isAgreementExempt ? <OnboardingStepDesc isActive={stepTesaAccepted}>TESA accepted</OnboardingStepDesc> : null}
        {!isAgreementExempt ? <OnboardingStepDesc isActive={stepSupplementAccepted}>Utility authorization</OnboardingStepDesc> : null}
        {showPaymentStep ? <OnboardingStepDesc isActive={stepPaymentAccepted}>Payment terms accepted</OnboardingStepDesc> : null}
        <OnboardingStepDesc isActive={stepRegistrationComplete}>Registration complete</OnboardingStepDesc>
              <OnboardingStepDesc isActive={isAccountInviteCreated}>Tenant created</OnboardingStepDesc>
              {!isNgom ? <OnboardingStepDesc isActive={isAccountInviteConsumed}>OneBill account created</OnboardingStepDesc> : null}
      </OnboardingSteps>
    </div>
  )
}

export default OnboardingMiniTimeline
